import { Box, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { actions } from '../../actions';
import { isRolePermissionError, useAuth } from '../../auth/AuthContext';
import { auth as authApi } from '../../services/api/auth';

/**
 * OAuth redirect target: Keycloak sends ?code=&state= here.
 * Exchanges code via Django (confidential client).
 */
export default function AuthCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { loginWithTokens, setPermissionDenied } = useAuth();
    const ran = useRef(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (ran.current) return;
        ran.current = true;

        const code = searchParams.get('code');
        const state = searchParams.get('state');
        if (!code || !state) {
            setError('Missing authorization code');
            setTimeout(() => navigate('/', { replace: true }), 2000);
            return;
        }

        axios
            .post('/api/auth/callback/', { code, state })
            .then((res) => {
                const user = authApi.buildUserFromResponse(res.data);
                loginWithTokens(user);
                dispatch(actions.notification.add.success(`Successfully connected as ${user.username}`));
                const next = res.data.next_path && res.data.next_path.startsWith('/') ? res.data.next_path : '/';
                navigate(next, { replace: true });
            })
            .catch((err) => {
                if (isRolePermissionError(err)) {
                    setPermissionDenied(true);
                    navigate('/', { replace: true });
                    return;
                }
                const msg = err.response?.data?.detail || err.message || 'Sign-in failed';
                setError(String(msg));
                setTimeout(() => navigate('/', { replace: true }), 3500);
            });
    }, [dispatch, navigate, searchParams, loginWithTokens, setPermissionDenied]);

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
            }}
        >
            {error ? (
                <Typography color='error' textAlign='center'>
                    {error}
                </Typography>
            ) : (
                <>
                    <CircularProgress />
                    <Typography color='text.secondary' textAlign='center'>
                        Completing sign-in…
                    </Typography>
                </>
            )}
        </Box>
    );
}
